import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty, noop } from "lodash-es";
import {
    createContext,
    PropsWithChildren,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import { useStoreServiceApi } from "@/api/hooks/storeService.hook";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { LocationService } from "@/services/location.service";

import {
    SearchItem,
    SearchItemType
} from "../StoreSearchInput/storeSearchInput.types";
import {
    STORE_PICKER_MESSAGE_ID,
    STORE_PICKER_STORES_LIMIT
} from "./storePicker.config";
import { StorePickerConfig } from "./types/storePickerConfig.type";
import { StorePickerType } from "./types/storePickerType.enum";

type StorePickerContextValue = {
    stores: Array<CloudStore> | null;
    loading: boolean;
    config: StorePickerConfig;
    searchItem: SearchItem | undefined;
    selectedStore: CloudStore | null;
    activeFeatures: Array<string>;
    activeFilters: Array<string>;
    setSearchItem: (searchItem: SearchItem | undefined) => void;
    setSelectedStore: (code: CloudStore | null) => void;
    toggleFeature: (feature: string) => void;
    toggleFilter: (filter: string) => void;
    submit: () => void;
    submitWithStore: (store: CloudStore) => void;
    clear: () => void;
};

type StorePickerContextProviderProps = PropsWithChildren<{
    disabled: boolean;
    sku?: string;
    config: StorePickerConfig;
    onSubmit: (store: CloudStore) => void;
}>;

export const StorePickerContext = createContext<
    StorePickerContextValue | undefined
>(undefined);
StorePickerContext.displayName = "StorePickerContext";

export const StorePickerContextProvider = ({
    disabled,
    sku,
    config,
    onSubmit,
    children
}: StorePickerContextProviderProps) => {
    const { t } = useGranite();

    const [selectedStore, setSelectedStore] = useState<CloudStore | null>(null);

    const [searchItem, setSearchItem] = useState<SearchItem | undefined>(
        undefined
    );

    const [activeFeatures, setActiveFeatures] = useState<Array<string>>([]);

    const [activeFilters, setActiveFilters] = useState<Array<string>>(
        config.initialActiveFilters || []
    );

    const [loadingLocation, setLoadingLocation] = useState(false);

    const [{ data, loading }, updateStores] = useStoreServiceApi<
        any,
        any,
        Array<CloudStore>
    >(
        {
            url: "/localized/stores",
            method: "GET"
        },
        {
            manual: true,
            useCache: true
        }
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const setCurrentLocationAsSearchItem = useCallback(() => {
        setLoadingLocation(true);

        LocationService.getCurrentPosition()
            .then(({ latitude, longitude }) => {
                setSearchItem({
                    value: "",
                    latitude: latitude,
                    longitude: longitude,
                    label: t("form.storePicker.myLocation"),
                    type: SearchItemType.Locality
                });
            })
            .catch((errorTk) => {
                broadcastMessage({
                    id: STORE_PICKER_MESSAGE_ID,
                    text: t(errorTk),
                    status: "danger",
                    dismissible: true
                });
            })
            .finally(() => setLoadingLocation(false));
    }, [t, setLoadingLocation, setSearchItem]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const stores = useMemo(() => {
        if (!isDefined(data) || loadingLocation || loading) return null;

        switch (config.type) {
            case StorePickerType.CUSTOM: {
                return data.filter(
                    (store) =>
                        isEmpty(config.storeCodes) ||
                        config.storeCodes.includes(store.code)
                );
            }
            default:
                return data;
        }
    }, [data, searchItem, config]);

    const selectedFeatures = useMemo(
        () => [...activeFeatures, ...(config.fixedFeatures ?? [])],
        [activeFeatures, config.fixedFeatures]
    );

    const selectedFilters = useMemo(
        () => [...activeFilters, ...(config.fixedFilters ?? [])],
        [activeFilters, config.fixedFilters]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const abortController = new AbortController();

        if (disabled) {
            return;
        } else if (
            !isDefined(searchItem) &&
            !isDefined(config.storeCode) &&
            isEmpty(activeFeatures) &&
            isEmpty(activeFilters)
        ) {
            // Ignore initial render
            return;
        } else if (!isDefined(searchItem) && !isDefined(config.storeCode)) {
            // The user selected a filter or feature without providing a search item
            // we set the search item to the current location
            setCurrentLocationAsSearchItem();
            return;
        }

        const limitSearchParams =
            config.type === StorePickerType.CUSTOM
                ? undefined
                : { limit: STORE_PICKER_STORES_LIMIT.toString() };

        const skuSearchParams = sku ? { sku: sku } : undefined;

        const includeProductAvailabilityParams = {
            includeProductAvailability: (
                config.displayStockLevel && !!sku
            ).toString()
        };

        const featuresSearchParams = !isEmpty(selectedFeatures)
            ? {
                  features: selectedFeatures.join(",")
              }
            : undefined;

        const filtersSearchParams = selectedFilters.reduce<
            Record<string, string>
        >(
            (acc, filter) => ({
                ...acc,
                [filter]: true.toString()
            }),
            {}
        );

        const storeIdSearchParams =
            (isDefined(searchItem) && searchItem.type === SearchItemType.Store
                ? { storeId: searchItem.value }
                : undefined) ??
            (isDefined(config.storeCode) &&
            searchItem?.type !== SearchItemType.Locality
                ? { storeId: config.storeCode }
                : undefined);

        const coordinatesSearchParams =
            searchItem?.type === SearchItemType.Locality &&
            isDefined(searchItem.latitude) &&
            isDefined(searchItem.longitude)
                ? {
                      latitude: searchItem.latitude.toString(),
                      longitude: searchItem.longitude.toString()
                  }
                : undefined;

        const storeTypeSearchParams = {
            storeType: config.filterStoreType
        };

        updateStores({
            signal: abortController.signal,
            params: {
                filterInactive: true.toString(),
                ...limitSearchParams,
                ...includeProductAvailabilityParams,
                ...skuSearchParams,
                ...featuresSearchParams,
                ...filtersSearchParams,
                ...storeIdSearchParams,
                ...coordinatesSearchParams,
                ...storeTypeSearchParams
            }
        })
            .then((stores) => {
                // Select store based on selected store in autocomplete
                if (
                    isDefined(searchItem) &&
                    searchItem.type === SearchItemType.Store
                ) {
                    const store = stores.data.find(
                        (store) => store.code === searchItem.value
                    );

                    setSelectedStore(store ?? null);
                }

                // Select initial store when opening the picker
                else if (config.type === StorePickerType.SELECT) {
                    const store = stores.data.find(
                        (store) => store.code === config.storeCode
                    );

                    setSelectedStore(store ?? null);
                }

                // Clear selection because this store is not in the list anymore
                else {
                    setSelectedStore(null);
                }

                return stores;
            })
            .catch(noop);

        return () => abortController.abort();
    }, [
        searchItem,
        selectedFeatures,
        activeFilters,
        config,
        disabled,
        setCurrentLocationAsSearchItem
    ]);

    const submit = useCallback(() => {
        if (!isDefined(selectedStore)) return;
        onSubmit(selectedStore);
    }, [selectedStore, onSubmit]);

    const submitWithStore = useCallback(
        (store: CloudStore) => {
            if (!isDefined(store)) return;
            setSelectedStore(store);
            onSubmit(store);
        },
        [onSubmit]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const clear = useCallback(() => {
        setSearchItem(undefined);
        setActiveFeatures([]);
        setActiveFilters([]);
    }, [setSearchItem, setActiveFeatures, setActiveFilters]);

    const toggleFeature = (feature: string) =>
        setActiveFeatures((currentFeatures) =>
            currentFeatures.includes(feature)
                ? currentFeatures.filter((f) => f !== feature)
                : [...currentFeatures, feature]
        );

    const toggleFilter = (filter: string) =>
        setActiveFilters((currentFilters) =>
            currentFilters.includes(filter)
                ? currentFilters.filter((f) => f !== filter)
                : [...currentFilters, filter]
        );

    return (
        <StorePickerContext
            value={{
                config: config,
                stores: stores,
                loading: loading || loadingLocation,
                searchItem: searchItem,
                selectedStore: selectedStore,
                activeFeatures: activeFeatures,
                activeFilters: activeFilters,
                setSearchItem: setSearchItem,
                setSelectedStore: setSelectedStore,
                toggleFeature: toggleFeature,
                toggleFilter: toggleFilter,
                submit: submit,
                submitWithStore: submitWithStore,
                clear: clear
            }}
        >
            {children}
        </StorePickerContext>
    );
};

export const useStorePickerContext = () => {
    const context = use<StorePickerContextValue | undefined>(
        StorePickerContext
    );

    if (!isDefined(context)) {
        throw new Error(`${StorePickerContext.displayName} is undefined.`);
    }

    return context;
};
